import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import moment from "moment";
import { db } from "../firebaseConfig";
import { confirmAndSendCompletionEmail } from "./completionEmail";

const RADIO_CIRCLE_STYLE = (selected) => ({
  width: 20,
  height: 20,
  borderRadius: "50%",
  border: selected ? "6px solid #1d4ed8" : "2px solid #94a3b8",
  background: "#fff",
  flexShrink: 0,
  boxSizing: "border-box",
  transition: "border 0.1s ease",
});

const RADIO_OPTION_STYLE = (selected) => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 14px",
  borderRadius: 10,
  border: selected ? "1px solid #1d4ed8" : "1px solid #e2e8f0",
  background: selected ? "#eff6ff" : "#fff",
  cursor: "pointer",
  fontSize: 15,
  fontWeight: 600,
  color: "#0f172a",
});

const RecurringEditScopeDialog = ({ onSelect }) => {
  const [choice, setChoice] = useState(null);

  const options = [
    { key: "occurrence", label: "Edit this job only" },
    { key: "future", label: "Edit this job and all future jobs" },
  ];

  return (
    <div
      className="modal-overlay"
      style={{ zIndex: 10090 }}
      onClick={() => onSelect("cancel")}
    >
      <div
        className="modal"
        style={{ width: "min(440px, 92vw)", padding: 24, zIndex: 10091 }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginTop: 0, marginBottom: 6 }}>Edit Recurring Job</h3>
        <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14 }}>
          This job repeats. Choose how this edit should apply.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {options.map((opt) => (
            <div
              key={opt.key}
              role="radio"
              aria-checked={choice === opt.key}
              tabIndex={0}
              style={RADIO_OPTION_STYLE(choice === opt.key)}
              onClick={() => setChoice(opt.key)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setChoice(opt.key);
              }}
            >
              <span style={RADIO_CIRCLE_STYLE(choice === opt.key)} />
              {opt.label}
            </div>
          ))}
        </div>

        <div className="button-group" style={{ marginTop: 20 }}>
          <button
            className="blue-button"
            disabled={!choice}
            style={!choice ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
            onClick={() => choice && onSelect(choice)}
          >
            Confirm
          </button>
          <button className="red-button" onClick={() => onSelect("cancel")}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// moment(undefined) does NOT return an invalid moment - it returns NOW, and
// .isValid() on it is true. Any `moment(maybeMissingField).isValid() ? ... : ...`
// therefore silently takes the "valid" branch with today's date. That is what
// made a single moved occurrence render one tile per day from its moved date
// all the way to today (500+ ghost rows for one 2025 occurrence, each one
// spanning every date filter on Home). ALWAYS parse optional dates with this.
const safeMoment = (value) => {
  if (!value) return null;
  const m = moment(value);
  return m.isValid() ? m : null;
};

// ---------------------------------------------------------------------------
// SERIES-LEVEL STATE - never per-occurrence.
//
// These fields live on the master recurring document and describe the WHOLE
// series. An occurrence override must never carry its own copy of them:
//   - saving one freezes a snapshot at edit time
//   - rendering spreads {...master, ...override}, so the frozen snapshot wins
//
// That combination is what made moved occurrences impossible to complete. The
// write to completedDates succeeded, but the next render pasted the override's
// stale (empty) completedDates back over the master's fresh one, so
// isOccurrenceCompletedFor() kept returning false forever.
//
// They are stripped on save (sanitizeOccurrenceOverridePayload) and re-pinned
// from the master on every render.
// ---------------------------------------------------------------------------
export const SERIES_LEVEL_FIELDS = [
  "completedDates",
  "invoicedDates",
  "excludedDates",
  "occurrenceOverrides",
  "occurrenceNotes",
  "recurrence",
  "status",
];

// Strip series-level state out of an occurrence override before it is written.
// Purely additive: existing overrides that already contain these fields are
// made harmless by the re-pinning in the two render functions below, so no
// data migration is required.
export const sanitizeOccurrenceOverridePayload = (payload = {}) => {
  const clean = { ...payload };
  SERIES_LEVEL_FIELDS.forEach((key) => delete clean[key]);
  return clean;
};

// Copy series-level state from the master record onto a rendered occurrence,
// AFTER the override has been spread. Called by both render paths.
const pinSeriesLevelFields = (target, master) => {
  const pinned = { ...target };
  SERIES_LEVEL_FIELDS.forEach((key) => {
    pinned[key] = master?.[key];
  });
  return pinned;
};

// The single source of truth for "when does this recurring series stop
// generating occurrences". Any page that expands a recurring job into
// calendar days MUST cap its loop with this value, or a job whose series
// was split/ended (see saveRecurringJobEdit's "future" mode and
// endRecurringSeries below) will keep rendering past its real end date and
// overlap with whatever replaced it - which is what caused duplicate tiles
// on the Calendar page.
export const getRecurrenceEndKey = (job) => {
  const recurrence = typeof job?.recurrence === "object" ? job.recurrence : {};
  const raw = recurrence.endsOn || recurrence.endDate || job?.recurrenceEndDate;
  if (!raw) return null;
  const m = moment(raw);
  return m.isValid() ? m.format("YYYY-MM-DD") : null;
};

// Dates an occurrence has been individually closed/removed for, without
// touching the rest of the series or its history.
export const getExcludedDateSet = (job) => {
  const excluded = Array.isArray(job?.excludedDates) ? job.excludedDates : [];
  return new Set(
    excluded
      .map((d) => (moment(d).isValid() ? moment(d).format("YYYY-MM-DD") : null))
      .filter(Boolean)
  );
};

const CloseJobScopeDialog = ({ onSelect }) => {
  const [choice, setChoice] = useState(null);

  const options = [
    {
      key: "occurrence",
      label: "Close this job only (this date)",
      hint: "Removes just this occurrence. The rest of the series is unaffected.",
    },
    {
      key: "future",
      label: "Close this job and all future jobs",
      hint: "Stops future occurrences but keeps all past history intact.",
    },
    {
      key: "all",
      label: "Delete all job history",
      hint: "Permanently deletes this job and every occurrence's history. This cannot be undone.",
    },
  ];

  return (
    <div
      className="modal-overlay"
      style={{ zIndex: 10090 }}
      onClick={() => onSelect("cancel")}
    >
      <div
        className="modal"
        style={{ width: "min(460px, 92vw)", padding: 24, zIndex: 10091 }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginTop: 0, marginBottom: 6 }}>Close Recurring Job</h3>
        <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14 }}>
          This job repeats. Choose how this should apply.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {options.map((opt) => (
            <div
              key={opt.key}
              role="radio"
              aria-checked={choice === opt.key}
              tabIndex={0}
              style={RADIO_OPTION_STYLE(choice === opt.key)}
              onClick={() => setChoice(opt.key)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setChoice(opt.key);
              }}
            >
              <span style={RADIO_CIRCLE_STYLE(choice === opt.key)} />
              <span>
                <div>{opt.label}</div>
                <div style={{ fontWeight: 400, fontSize: 12.5, color: "#64748b", marginTop: 2 }}>
                  {opt.hint}
                </div>
              </span>
            </div>
          ))}
        </div>

        <div className="button-group" style={{ marginTop: 20 }}>
          <button
            className={choice === "all" ? "red-button" : "blue-button"}
            disabled={!choice}
            style={!choice ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
            onClick={() => choice && onSelect(choice)}
          >
            Confirm
          </button>
          <button className="grey-button" onClick={() => onSelect("cancel")}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export const isRecurringJobRecord = (job) => {
  const type =
    typeof job?.recurrence === "object"
      ? job?.recurrence?.type
      : job?.recurrence || job?.reccurance || job?.recurring || job?.repeat;

  return Boolean(type && String(type).toLowerCase().trim() !== "none");
};

// ---------------------------------------------------------------------------
// OCCURRENCE IDENTITY - single source of truth
//
// Every per-occurrence array on a recurring job doc (completedDates,
// invoicedDates, excludedDates, occurrenceNotes, occurrenceOverrides) is keyed
// by the occurrence's ORIGINAL date - the date the recurrence rule generated.
// That key is stable: an occurrence can be moved (or moved twice) by an
// occurrence-level edit, but its identity in the series never changes.
//
// Using occurrenceDate/startDate instead is what caused completed occurrences
// to reappear: applyOccurrenceOverrideToRenderedJob rewrites occurrenceDate to
// the MOVED date, so a write keyed off it never matches the ORIGINAL date the
// expansion filters check.
//
// ALWAYS use this to read or write per-occurrence state. Do not re-derive it.
// ---------------------------------------------------------------------------
export const getRecurringOccurrenceKey = (job) => {
  const raw =
    job?.originalOccurrenceDate ||
    job?.occurrenceDate ||
    job?.__renderDate ||
    job?.startDate;

  // moment(undefined) silently returns "now", which would key an occurrence to
  // today and quietly complete the wrong day. Return "" so callers can bail.
  if (!raw) return "";

  const m = moment(raw);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

const toOccurrenceKey = (value) => {
  if (!value) return "";
  if (typeof value === "object" && value?.seconds) {
    return moment(value.seconds * 1000).format("YYYY-MM-DD");
  }
  if (typeof value === "string") {
    const s = value.slice(0, 10);
    return moment(s, "YYYY-MM-DD", true).isValid() ? s : "";
  }
  const m = moment(value);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

export const getOccurrenceKeySet = (job, field) =>
  new Set((job?.[field] || []).map(toOccurrenceKey).filter(Boolean));

export const isOccurrenceCompletedFor = (job) => {
  if (!job) return false;

  if (!isRecurringJobRecord(job)) {
    return String(job.status || "").trim().toLowerCase() === "completed";
  }

  const key = getRecurringOccurrenceKey(job);
  if (!key) return false;

  return getOccurrenceKeySet(job, "completedDates").has(key);
};

export const isOccurrenceInvoicedFor = (job) => {
  if (!job) return false;

  if (!isRecurringJobRecord(job)) {
    const status = String(job.status || "").trim().toLowerCase();
    return status === "invoice sent" || status === "invoiced";
  }

  const key = getRecurringOccurrenceKey(job);
  if (!key) return false;

  return getOccurrenceKeySet(job, "invoicedDates").has(key);
};

export const isOccurrenceClosedFor = (job) => {
  if (!job) return false;

  if (isRecurringJobRecord(job)) {
    return isOccurrenceCompletedFor(job) || isOccurrenceInvoicedFor(job);
  }

  const status = String(job.status || "").trim().toLowerCase();
  return status === "completed" || status === "invoice sent";
};

// ---------------------------------------------------------------------------
// Shared writer for "mark completed".
//
// Recurring: append ONLY this occurrence's key to completedDates. The shared
// `status` field is never touched - writing it closes the entire series on
// every page that gates on status.
//
// Non-recurring: set status AND record the date, as before.
//
// Returns the patch to merge into local state, or null if nothing was written.
// ---------------------------------------------------------------------------
export const markOccurrenceCompleted = async (
  job,
  { collectionName = "jobs", notifyClient = true } = {}
) => {
  if (!job?.id) return null;

  const jobRef = doc(db, collectionName, job.id);
  const recurring = isRecurringJobRecord(job);
  const realOccurrenceKey = getRecurringOccurrenceKey(job);

  // A recurring job with no date at all (no start/occurrence date yet) can
  // still be completed. It is recorded under TODAY and flagged with
  // `needsOccurrenceDate` / `undatedCompletions` so the office can add the
  // real date later (Edit Job) - instead of blocking the tech in the field.
  const dateMissing = recurring && !realOccurrenceKey;
  const occurrenceKey = realOccurrenceKey || moment().format("YYYY-MM-DD");

  // Read the live doc first. Rendered occurrences are copies of the master and
  // can be stale, so writing the array from the local copy could silently drop
  // dates another view already recorded.
  const snap = await getDoc(jobRef);
  const current = snap.exists() ? snap.data() : job;

  const nextCompleted = Array.from(
    new Set(
      [
        ...(current.completedDates || []).map(toOccurrenceKey).filter(Boolean),
        occurrenceKey,
      ].filter(Boolean)
    )
  );

  const update = recurring
    ? { completedDates: nextCompleted, updatedAt: serverTimestamp() }
    : { status: "Completed", completedDates: nextCompleted, updatedAt: serverTimestamp() };

  let undatedCompletions = current.undatedCompletions || [];
  if (dateMissing) {
    undatedCompletions = Array.from(new Set([...undatedCompletions, occurrenceKey]));
    update.undatedCompletions = undatedCompletions;
    update.needsOccurrenceDate = true;
  }

  await updateDoc(jobRef, update);

  // ---------------------------------------------------------------------
  // Client completion notice.
  //
  // Deliberately AFTER the write. The completion is recorded whatever the
  // user answers, and a bounced email can never roll back a completed job.
  // Only the jobs collection notifies - other collections reuse this writer
  // for their own records and have no client attached.
  // ---------------------------------------------------------------------
  let emailResult = { sent: false };

  if (notifyClient && collectionName === "jobs") {
    try {
      emailResult = await confirmAndSendCompletionEmail(
        { ...current, id: job.id, ...job },
        { occurrenceKey }
      );
    } catch (err) {
      console.error("Completion email step failed (job is still completed):", err);
      emailResult = { sent: false, error: err?.message || String(err) };
    }
  }

  const patch = recurring
    ? { completedDates: nextCompleted }
    : { status: "Completed", completedDates: nextCompleted };
  if (dateMissing) {
    patch.undatedCompletions = undatedCompletions;
    patch.needsOccurrenceDate = true;
  }

  return {
    occurrenceKey,
    recurring,
    dateMissing,
    email: emailResult,
    // merge this into the master job in local state
    patch,
  };
};

export const chooseRecurringEditScope = () =>
  new Promise((resolve) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);

    const finish = (scope) => {
      root.unmount();
      container.remove();
      resolve(scope);
    };

    root.render(<RecurringEditScopeDialog onSelect={finish} />);
  });

export const chooseCloseJobScope = () =>
  new Promise((resolve) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);

    const finish = (scope) => {
      root.unmount();
      container.remove();
      resolve(scope);
    };

    root.render(<CloseJobScopeDialog onSelect={finish} />);
  });

export const cleanRecurringEditPayload = (editedJob = {}) => {
  const payload = { ...editedJob };

  [
    "id",
    "__renderDate",
    "occurrenceDate",
    "originalOccurrenceDate",
    "occurrenceStatus",
    "companyUseMode",
    "newFiles",
    "tempCustomDate",
  ].forEach((key) => delete payload[key]);

  return payload;
};

export const applyOccurrenceOverrideToRenderedJob = (renderedJob) => {
  if (!renderedJob || !isRecurringJobRecord(renderedJob)) return renderedJob;

  // Unscheduled recurring jobs have no date at all. moment(undefined) would
  // resolve to today and could match today's override by accident.
  const originalOccurrenceMoment = safeMoment(
    renderedJob.originalOccurrenceDate ||
      renderedJob.occurrenceDate ||
      renderedJob.__renderDate ||
      renderedJob.startDate
  );
  if (!originalOccurrenceMoment) return renderedJob;

  const originalOccurrenceDate = originalOccurrenceMoment.format("YYYY-MM-DD");

  const override = renderedJob.occurrenceOverrides?.[originalOccurrenceDate];

  if (!override) return renderedJob;

  const originalRenderDate = moment(
    renderedJob.__renderDate || originalOccurrenceDate
  );
  const originalStart = moment(originalOccurrenceDate);
  const movedStart = moment(
    override.startDate || override.occurrenceDate || originalOccurrenceDate
  );

  const dayOffset = originalRenderDate.diff(originalStart, "days");
  const movedRenderDate = movedStart.clone().add(dayOffset, "days");

  // Series-level state (completedDates, excludedDates, invoicedDates, status,
  // ...) must come from the master record, NOT from the override's frozen
  // snapshot - otherwise marking this occurrence complete never sticks.
  return pinSeriesLevelFields(
    {
      ...renderedJob,
      ...override,
      id: renderedJob.id,
      originalOccurrenceDate,
      occurrenceDate: movedStart.format("YYYY-MM-DD"),
      startDate: movedStart.format("YYYY-MM-DD"),
      __renderDate: movedRenderDate.format("YYYY-MM-DD"),
      isOccurrenceOverride: true,
    },
    renderedJob
  );
};


export const getOccurrenceOverrideRenderJobs = (masterJob) => {
  if (!masterJob || !isRecurringJobRecord(masterJob)) return [];

  const overrides = masterJob.occurrenceOverrides || {};
  const baseStart = safeMoment(masterJob.startDate);
  const baseEnd = safeMoment(masterJob.endDate) || baseStart;
  const defaultSpanDays =
    baseStart && baseEnd ? Math.max(baseEnd.diff(baseStart, "days"), 0) : 0;

  // An occurrence closed on its own ("Close this job only") is keyed by its
  // ORIGINAL date. The normal expansion paths already skip these; the moved
  // path never did, so closing a moved occurrence appeared to do nothing.
  const excludedSet = getExcludedDateSet(masterJob);

  // A series that was split or ended still keeps its old overrides on the
  // document. Without this cap they keep rendering past the series end date.
  const seriesEndKey = getRecurrenceEndKey(masterJob);

  return Object.entries(overrides).flatMap(
    ([originalOccurrenceDate, override]) => {
      const originalKey =
        safeMoment(originalOccurrenceDate)?.format("YYYY-MM-DD") || "";
      if (!originalKey) return [];

      if (excludedSet.has(originalKey)) return [];
      if (seriesEndKey && originalKey > seriesEndKey) return [];

      const movedStart = safeMoment(
        override?.startDate ||
          override?.occurrenceDate ||
          originalOccurrenceDate
      );

      if (!movedStart) return [];

      // NOTE: safeMoment, not moment(). override.endDate is frequently absent,
      // and moment(undefined) returns TODAY as a *valid* moment - which turned
      // spanDays into "days since the occurrence was moved" and rendered one
      // ghost tile per day from then until now.
      const overrideEnd = safeMoment(override?.endDate);
      const spanDays = overrideEnd
        ? Math.max(overrideEnd.diff(movedStart, "days"), 0)
        : defaultSpanDays;

      return Array.from({ length: spanDays + 1 }, (_, dayOffset) =>
        pinSeriesLevelFields(
          {
            ...masterJob,
            ...override,
            id: masterJob.id,
            originalOccurrenceDate: originalKey,
            occurrenceDate: movedStart.format("YYYY-MM-DD"),
            startDate: movedStart.format("YYYY-MM-DD"),
            endDate:
              spanDays > 0
                ? movedStart.clone().add(spanDays, "days").format("YYYY-MM-DD")
                : override?.endDate || movedStart.format("YYYY-MM-DD"),
            __renderDate: movedStart
              .clone()
              .add(dayOffset, "days")
              .format("YYYY-MM-DD"),
            isOccurrenceOverride: true,
          },
          masterJob
        )
      );
    }
  );
};

export const mergeRenderedRecurringJobs = (jobs = []) => {
  const map = new Map();

  const normalizeDateKey = (value) => {
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
  };

  const getSeriesKey = (job) => {
    if (!isRecurringJobRecord(job)) {
      return String(job?.id || job?.wo || "job").trim().toLowerCase();
    }

    const recurrenceType =
      typeof job?.recurrence === "object"
        ? job.recurrence?.type
        : job?.recurrence || job?.reccurance || job?.recurring || job?.repeat;

    return [
      job?.wo || job?.workOrder || job?.workOrderNumber || "",
      job?.siteName || job?.property || "",
      job?.serviceType || "",
      normalizeDateKey(job?.originalSeriesStartDate || job?.seriesStartDate || job?.startDate),
      recurrenceType || "",
    ]
      .map((value) => String(value || "").trim().toLowerCase())
      .join("|");
  };

  jobs.forEach((job) => {
    if (!job) return;

    const originalOccurrenceKey = normalizeDateKey(
      job.originalOccurrenceDate ||
        job.occurrenceDate ||
        job.__renderDate ||
        job.startDate
    );
    const renderDateKey = normalizeDateKey(job.__renderDate || job.startDate);
    const key = [getSeriesKey(job), originalOccurrenceKey, renderDateKey].join("__");

    // Override render jobs are the most specific representation of an
    // occurrence, so prefer them when both normal and override paths produce
    // the same calendar tile.
    const existing = map.get(key);
    if (!existing || job.isOccurrenceOverride) {
      map.set(key, job);
    }
  });

  return Array.from(map.values());
};

export const saveRecurringJobEdit = async ({
  originalJob,
  editedJob,
  // Optional: "occurrence" | "future". When given, the scope dialog is
  // skipped (Calendar drag-and-drop asks its own "move" question first).
  scope: presetScope = null,
}) => {
  if (!originalJob?.id || !editedJob) {
    throw new Error("A job and edited job are required.");
  }

  const payload = cleanRecurringEditPayload(editedJob);
  const recurring = isRecurringJobRecord(originalJob);

  if (!recurring) {
    await updateDoc(doc(db, "jobs", originalJob.id), payload);

    return {
      mode: "single",
      updatedJob: { ...originalJob, ...payload, id: originalJob.id },
    };
  }

  const scope = presetScope || (await chooseRecurringEditScope());

  if (scope === "cancel") {
    return { mode: "cancel" };
  }

  const occurrenceKey = getRecurringOccurrenceKey(originalJob);

  if (scope === "occurrence") {
    const jobRef = doc(db, "jobs", originalJob.id);
    const snap = await getDoc(jobRef);
    const current = snap.exists() ? snap.data() : originalJob;

    // Strip series-level state so this override stores ONLY what is genuinely
    // specific to this one occurrence. Without this the override froze a copy
    // of completedDates/excludedDates/status, which then won on every render.
    const occurrencePayload = sanitizeOccurrenceOverridePayload(payload);

    const occurrenceOverrides = {
      ...(current.occurrenceOverrides || {}),
      [occurrenceKey]: {
        ...occurrencePayload,
        originalOccurrenceDate: occurrenceKey,
        occurrenceDate: payload.startDate || occurrenceKey,
        startDate: payload.startDate || occurrenceKey,
        updatedAt: new Date().toISOString(),
      },
    };

    await updateDoc(jobRef, { occurrenceOverrides });

    return {
      mode: "occurrence",
      occurrenceKey,
      updatedJob: pinSeriesLevelFields(
        {
          ...originalJob,
          ...occurrenceOverrides[occurrenceKey],
          id: originalJob.id,
          originalOccurrenceDate: occurrenceKey,
          isOccurrenceOverride: true,
        },
        { ...current, occurrenceOverrides }
      ),
      masterJob: {
        ...current,
        occurrenceOverrides,
        id: originalJob.id,
      },
    };
  }

  const oldJobRef = doc(db, "jobs", originalJob.id);
  const oldSnap = await getDoc(oldJobRef);
  const oldJob = oldSnap.exists() ? oldSnap.data() : originalJob;

  const oldRecurrence =
    typeof oldJob.recurrence === "object"
      ? { ...oldJob.recurrence }
      : { type: oldJob.recurrence || "none" };

  const originalSeriesEnd = oldRecurrence.endsOn || "";
  const splitStart = moment(payload.startDate || occurrenceKey).format("YYYY-MM-DD");
  const oldSeriesEnd = moment(occurrenceKey)
    .subtract(1, "day")
    .format("YYYY-MM-DD");

  await updateDoc(oldJobRef, {
    recurrence: {
      ...oldRecurrence,
      endsOn: oldSeriesEnd,
    },
    seriesSplitAfter: oldSeriesEnd,
    updatedAt: serverTimestamp(),
  });

  const newSeriesData = {
    ...oldJob,
    ...payload,
    startDate: splitStart,
    recurrence: {
      ...oldRecurrence,
      ...(payload.recurrence || {}),
      endsOn:
        payload?.recurrence?.endsOn !== undefined
          ? payload.recurrence.endsOn
          : originalSeriesEnd,
    },
    seriesRootId: oldJob.seriesRootId || originalJob.id,
    previousSeriesId: originalJob.id,
    originalWO: oldJob.wo || "",
    wo: oldJob.wo || "",
    occurrenceOverrides: {},
    completedDates: [],
    invoicedDates: [],
    excludedDates: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  delete newSeriesData.id;
  delete newSeriesData.__renderDate;
  delete newSeriesData.occurrenceDate;
  delete newSeriesData.originalOccurrenceDate;
  delete newSeriesData.occurrenceStatus;

  const newDocRef = await addDoc(collection(db, "jobs"), newSeriesData);

  return {
    mode: "future",
    oldJob: {
      ...oldJob,
      recurrence: {
        ...oldRecurrence,
        endsOn: oldSeriesEnd,
      },
      id: originalJob.id,
    },
    newJob: {
      ...newSeriesData,
      id: newDocRef.id,
    },
  };
};

// Shared "Close Job" action for every page that offers a delete/close
// button. Non-recurring jobs get a plain confirm and a real delete, exactly
// like before. Recurring jobs get the scope dialog:
//   - "occurrence": closes just this date (adds it to excludedDates) -
//     equivalent to "delete this one job only, this recurring date".
//   - "future": ends the series after this occurrence (sets
//     recurrence.endsOn) WITHOUT deleting the document or any past
//     history - this is "end the recurring job without deleting its
//     history" / "delete this job and future jobs".
//   - "all": permanently deletes the job document and all of its history.
export const closeOrDeleteJob = async ({ job, collectionName = "jobs" }) => {
  if (!job?.id) {
    throw new Error("A job is required.");
  }

  const recurring = isRecurringJobRecord(job);

  if (!recurring) {
    const confirmed = window.confirm(
      "Close out this job? This will permanently delete it and cannot be undone."
    );
    if (!confirmed) return { mode: "cancel" };

    await deleteDoc(doc(db, collectionName, job.id));
    return { mode: "deleted", jobId: job.id };
  }

  const scope = await chooseCloseJobScope();
  if (scope === "cancel") return { mode: "cancel" };

  const jobRef = doc(db, collectionName, job.id);

  if (scope === "all") {
    await deleteDoc(jobRef);
    return { mode: "deleted", jobId: job.id };
  }

  const occurrenceKey = getRecurringOccurrenceKey(job);
  const snap = await getDoc(jobRef);
  const current = snap.exists() ? snap.data() : job;

  if (scope === "occurrence") {
    const excludedDates = Array.from(
      new Set([...(current.excludedDates || []), occurrenceKey])
    );

    await updateDoc(jobRef, { excludedDates, updatedAt: serverTimestamp() });

    return {
      mode: "occurrence",
      occurrenceKey,
      updatedJob: { ...job, ...current, excludedDates, id: job.id },
    };
  }

  // scope === "future": stop the series after this occurrence, keep history.
  const currentRecurrence =
    typeof current.recurrence === "object"
      ? { ...current.recurrence }
      : { type: current.recurrence || "none" };

  const newEnd = moment(occurrenceKey).subtract(1, "day").format("YYYY-MM-DD");
  const existingEnd = currentRecurrence.endsOn
    ? moment(currentRecurrence.endsOn)
    : null;

  // If the series was already set to end earlier than this occurrence,
  // don't push the end date later.
  const finalEnd =
    existingEnd && existingEnd.isValid() && existingEnd.isBefore(moment(newEnd), "day")
      ? currentRecurrence.endsOn
      : newEnd;

  const updatedRecurrence = { ...currentRecurrence, endsOn: finalEnd };

  await updateDoc(jobRef, {
    recurrence: updatedRecurrence,
    seriesEndedAt: new Date().toISOString(),
    updatedAt: serverTimestamp(),
  });

  return {
    mode: "future",
    updatedJob: {
      ...job,
      ...current,
      recurrence: updatedRecurrence,
      id: job.id,
    },
  };
};
